import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

interface ReportLogoHeaderProps {
  workspaceId?: string;
}

interface ReportLogoSettings {
  showOrganizationLogo: boolean;
  showWorkspaceLogo: boolean;
  logoPosition: 'left' | 'center' | 'right';
}

interface LogoUrls {
  organization: string | null;
  workspace: string | null;
}

/**
 * ReportLogoHeader - Displays organization and/or workspace logos on reports
 * 
 * This component fetches the logo settings for a workspace and displays
 * the configured logos. It uses CSS constraints to ensure large images
 * don't overflow the container.
 */
const ReportLogoHeader: React.FC<ReportLogoHeaderProps> = ({ workspaceId }) => {
  // Fetch logo settings and URLs
  const { data, isLoading, error } = useQuery({
    queryKey: ['report-logo-settings', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspace-settings/${workspaceId}/report-logo-settings`);
      return response.data.data;
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    retry: 1, // Only retry once on failure
    onError: (err: any) => {
      console.error('[ReportLogoHeader] Failed to fetch logo settings:', err?.response?.data || err?.message || err);
    }
  });

  // Log the response for debugging
  if (data) {
    console.debug('[ReportLogoHeader] Logo data:', {
      settings: data.settings,
      logoUrls: data.logoUrls,
      logoFiles: data.logoFiles
    });
  }

  // Don't render anything while loading, on error, or if no data
  if (isLoading || error || !data) {
    return null;
  }

  const settings: ReportLogoSettings = data.settings || {
    showOrganizationLogo: true,
    showWorkspaceLogo: false,
    logoPosition: 'left'
  };

  const logoUrls: LogoUrls = data.logoUrls || {
    organization: null,
    workspace: null
  };

  // Determine which logos to show
  const showOrgLogo = settings.showOrganizationLogo && logoUrls.organization;
  const showWsLogo = settings.showWorkspaceLogo && logoUrls.workspace;

  // If no logos to show, don't render anything
  if (!showOrgLogo && !showWsLogo) {
    return null;
  }

  // Determine alignment based on position setting
  const alignmentClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end'
  }[settings.logoPosition] || 'justify-start';

  return (
    <div className={`flex items-center gap-4 mb-4 ${alignmentClass}`}>
      {showOrgLogo && (
        <div className="flex-shrink-0 w-36 h-16 flex items-center justify-center">
          <img
            src={logoUrls.organization!}
            alt="Organization Logo"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
      {showWsLogo && (
        <div className="flex-shrink-0 w-36 h-16 flex items-center justify-center">
          <img
            src={logoUrls.workspace!}
            alt="Workspace Logo"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default ReportLogoHeader;

